import type { Line } from './lines'
import { matchHeading, type SectionId } from './sections'
import { isBulletLine, stripBullet } from './extract'
import {
  CITY_KEYWORDS,
  containsKeyword,
  DEGREE_KEYWORDS,
  EMAIL_REGEX,
  GPA_LABELS,
  JOB_TITLE_KEYWORDS,
  LOCATION_REGEX,
  PHONE_REGEX,
  SCHOOL_KEYWORDS,
  URL_REGEX,
} from './keywords'
import type {
  ParsedEducation,
  ParsedProfile,
  ParsedResume,
  ParsedWork,
} from './types'

type SectionBuckets = Record<SectionId, Line[]>

function emptyBuckets(): SectionBuckets {
  return {
    summary: [],
    experience: [],
    education: [],
    skills: [],
    organization: [],
    certifications: [],
    projects: [],
  }
}

// Lines before the first heading = profile; the rest are bucketed by heading.
function bucketSections(lines: Line[]): {
  profile: Line[]
  sections: SectionBuckets
} {
  const sections = emptyBuckets()
  const profile: Line[] = []
  let current: SectionId | null = null

  for (const line of lines) {
    const heading = matchHeading(line.text)
    if (heading) {
      current = heading
      continue
    }
    if (current === null) profile.push(line)
    else sections[current].push(line)
  }

  return { profile, sections }
}

const MONTHS =
  'jan|feb|mar|apr|mei|may|jun|jul|agu|aug|sep|okt|oct|nov|des|dec'
const DATE_TOKEN = `(?:(?:${MONTHS})[a-z]*\\.?\\s*)?(?:19|20)\\d{2}`
const DATE_END = `(?:${DATE_TOKEN}|present|sekarang|now|saat ini|hingga kini|kini)`
const DATE_SEP = `\\s*(?:[-–—]|s\\.d\\.|sampai|hingga|\\bto\\b)\\s*`
const DATE_RANGE_RE = new RegExp(`${DATE_TOKEN}${DATE_SEP}${DATE_END}`, 'i')
const DATE_SINGLE_RE = new RegExp(
  `(?:expected(?:\\sgraduation)?[^\\d]*)?${DATE_TOKEN}`,
  'i',
)

export function extractDate(text: string): string {
  const range = text.match(DATE_RANGE_RE)
  if (range) return range[0].replace(/\s+/g, ' ').trim()
  const single = text.match(DATE_SINGLE_RE)
  return single ? single[0].replace(/\s+/g, ' ').trim() : ''
}

const GPA_RE = new RegExp(
  `(?:${GPA_LABELS.join('|')})\\s*[:=]?\\s*(\\d(?:[.,]\\d{1,2})?)(?:\\s*/\\s*\\d(?:[.,]\\d{1,2})?)?`,
  'i',
)

export function extractGpa(text: string): string {
  const match = text.match(GPA_RE)
  return match ? match[0].replace(/\s+/g, ' ').trim() : ''
}

function extractLocation(text: string): string {
  const match = text.match(LOCATION_REGEX)
  if (match) return match[0].trim()
  if (containsKeyword(text, CITY_KEYWORDS)) {
    // Return the city token if a comma pattern was absent.
    const lower = text.toLowerCase()
    const city = CITY_KEYWORDS.find((c) => lower.includes(c))
    return city ? city.replace(/\b\w/g, (l) => l.toUpperCase()) : ''
  }
  return ''
}

// Score profile lines to pick the candidate name.
function extractName(profileLines: Line[]): string {
  const maxFont = Math.max(1, ...profileLines.map((l) => l.fontSize))
  let best = ''
  let bestScore = -Infinity

  profileLines.slice(0, 6).forEach((line, index) => {
    const text = line.text.trim()
    if (!text) return
    if (EMAIL_REGEX.test(text) || PHONE_REGEX.test(text) || URL_REGEX.test(text))
      return
    if (/\d/.test(text)) return
    if (
      containsKeyword(text, SCHOOL_KEYWORDS) ||
      containsKeyword(text, DEGREE_KEYWORDS)
    )
      return

    const words = text.split(/\s+/)
    let score = 0
    if (words.length >= 2 && words.length <= 4) score += 3
    else if (words.length === 1) score -= 1
    else score -= 2
    if (line.bold) score += 2
    if (line.fontSize >= maxFont - 0.5) score += 2
    if (words.every((w) => /^[A-ZÀ-Ý]/.test(w))) score += 1
    score += Math.max(0, 3 - index) * 0.5

    if (score > bestScore) {
      bestScore = score
      best = text
    }
  })

  return bestScore >= 3 ? best : ''
}

function joinText(lines: Line[]): string {
  return lines.map((l) => l.text).join(' ')
}

function extractProfile(
  profileLines: Line[],
  summaryLines: Line[],
  fullText: string,
): ParsedProfile {
  const profileText = joinText(profileLines)

  const email = (fullText.match(EMAIL_REGEX) ?? [''])[0]
  const phone = (fullText.match(PHONE_REGEX) ?? [''])[0].trim()
  const link = (profileText.match(URL_REGEX) ?? fullText.match(URL_REGEX) ?? [
    '',
  ])[0]

  let summary = ''
  if (summaryLines.length > 0) {
    summary = summaryLines.map((l) => stripBullet(l.text)).join(' ').trim()
  }

  return {
    name: extractName(profileLines),
    email,
    phone,
    location: extractLocation(profileText),
    link: /@/.test(link) ? '' : link, // avoid mistaking email for a link
    summary,
  }
}

// Split a section's lines into entries. A non-bullet line that starts a new
// block (bold, or the first line) begins an entry; bullets attach to it.
type RawEntry = { headers: Line[]; descriptions: string[] }

function splitEntries(
  lines: Line[],
  isNewEntry: (line: Line) => boolean,
): RawEntry[] {
  const entries: RawEntry[] = []
  let current: RawEntry | null = null

  for (const line of lines) {
    if (isBulletLine(line.text)) {
      if (current) current.descriptions.push(stripBullet(line.text))
      continue
    }
    if (!current || isNewEntry(line)) {
      current = { headers: [line], descriptions: [] }
      entries.push(current)
    } else {
      current.headers.push(line)
    }
  }

  return entries
}

function extractEducations(lines: Line[]): ParsedEducation[] {
  if (lines.length === 0) return []
  const entries = splitEntries(lines, (line) =>
    containsKeyword(line.text, SCHOOL_KEYWORDS),
  )

  return entries.map((entry) => {
    const headerText = joinText(entry.headers)
    const schoolLine =
      entry.headers.find((l) => containsKeyword(l.text, SCHOOL_KEYWORDS)) ??
      entry.headers[0]
    const degreeLine = entry.headers.find((l) =>
      containsKeyword(l.text, DEGREE_KEYWORDS),
    )

    return {
      school: stripDate(schoolLine.text),
      degree: degreeLine ? stripDate(degreeLine.text) : '',
      gpa: extractGpa(headerText),
      date: extractDate(headerText),
      descriptions: entry.descriptions,
    }
  })
}

function stripDate(text: string): string {
  const date = extractDate(text)
  if (!date) return text.trim()
  return text.replace(date, '').replace(/\s{2,}/g, ' ').trim()
}

function extractWork(lines: Line[]): ParsedWork[] {
  if (lines.length === 0) return []
  // A bold non-bullet line starts a new role; non-bold header lines (e.g. a
  // title under the company) attach to the current role.
  const entries = splitEntries(lines, (line) => line.bold)

  return entries.map((entry) => {
    const date = extractDate(joinText(entry.headers))
    const headersNoDate = entry.headers
      .map((l) => ({ line: l, text: stripDate(l.text) }))
      .filter((h) => h.text.length > 0)

    const titleHeader = headersNoDate.find((h) =>
      containsKeyword(h.text, JOB_TITLE_KEYWORDS),
    )
    const companyHeader = headersNoDate.find((h) => h !== titleHeader)

    return {
      company: companyHeader?.text ?? '',
      jobTitle: titleHeader?.text ?? '',
      date,
      descriptions: entry.descriptions,
    }
  })
}

function extractSkills(lines: Line[]): string[] {
  const raw = lines
    .map((l) => stripBullet(l.text))
    .join(', ')
    // strip a leading "Computer:" style label
    .replace(/^[A-Za-z ]{0,20}:\s*/, '')
  return raw
    .split(/[,;•|]/)
    .map((s) => s.trim())
    .filter((s) => s.length > 1 && s.length < 60)
    .slice(0, 30)
}

export function extractResume(lines: Line[]): ParsedResume {
  const { profile, sections } = bucketSections(lines)
  const fullText = joinText(lines)

  return {
    profile: extractProfile(profile, sections.summary, fullText),
    educations: extractEducations(sections.education),
    // Treat the organization section as additional experience entries.
    workExperiences: [
      ...extractWork(sections.experience),
      ...extractWork(sections.organization),
    ],
    skills: extractSkills(sections.skills),
  }
}
